import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Rocket, ShieldCheck, Joystick, GraduationCap, Gift } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ROLES = [
  { id: "creator", label: "Creator", icon: Rocket, desc: "Build & publish games" },
  { id: "parent", label: "Parent", icon: ShieldCheck, desc: "Create kid accounts" },
  { id: "educator", label: "Teacher", icon: GraduationCap, desc: "Run classes & assignments" },
  { id: "player", label: "Player", icon: Joystick, desc: "Play & compete" },
];

export default function Register() {
  const { register, formatApiError } = useAuth();
  const { setMode } = useMode();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from ? `${location.state.from.pathname}${location.state.from.search || ""}` : "/app";
  const refCode = new URLSearchParams(location.search).get("ref");
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "creator" });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { setMode("studio"); }, [setMode]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      if (refCode) {
        try { await api.post("/growth/referral/attribute", { code: refCode }); } catch { /* best-effort */ }
      }
      toast.success("Account created! Let's make some games 🚀");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#05060a] text-white">
      <div className="hidden lg:flex flex-1 relative items-center justify-center">
        <img src="/brand/gv-hero-bg.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-[#05060a]" />
        <div className="relative z-10 text-center px-10">
          <img src="/brand/gameview-logo-blend.png" alt="GameView Studio" className="w-72 mx-auto drop-shadow-[0_8px_40px_rgba(59,158,255,0.35)]" />
          <p className="mt-4 text-white/60 max-w-sm mx-auto">Join the AI game creation platform for the next generation.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 overflow-y-auto">
        <div className="w-full max-w-sm py-8 animate-rise">
          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" /></div>
          <h1 className="font-display text-3xl font-bold mb-1">Create your account</h1>
          <p className="text-white/50 mb-6 text-sm">Free forever to start. No card needed.</p>
          {refCode && (
            <div data-testid="referral-invite-banner"
              className="mb-5 flex items-center gap-2.5 rounded-xl border border-fuchsia-500/30 bg-fuchsia-500/10 px-3.5 py-2.5">
              <Gift className="w-4 h-4 text-fuchsia-400 shrink-0" />
              <p className="text-xs text-white/80">You were invited by a friend — you'll both earn <b className="text-fuchsia-300">bonus AI credits</b> when you build your first game! 🎁</p>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>I am a…</Label>
              <div className="grid grid-cols-2 gap-2 mt-1.5">
                {ROLES.map((r) => (
                  <button key={r.id} type="button" onClick={() => set("role", r.id)} data-testid={`role-${r.id}`}
                    className={`rounded-xl p-3 text-center border transition-all ${form.role === r.id ? "border-[#3b9eff] bg-[#3b9eff]/15" : "border-white/10 bg-white/5 hover:bg-white/10"}`}>
                    <r.icon className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs font-bold">{r.label}</div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-white/40 mt-1.5">{ROLES.find((r) => r.id === form.role)?.desc}. Kids accounts are created by parents.</p>
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input id="name" data-testid="reg-name" value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="reg-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="reg-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="mt-1.5 bg-white/5 border-white/10" />
            </div>
            <Button type="submit" disabled={loading} data-testid="reg-submit"
              className="w-full rounded-full py-6 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] hover:opacity-90 brand-glow">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-white/50 text-center">
            Already have an account? <Link to="/login" state={location.state} className="text-[#3b9eff] font-semibold" data-testid="goto-login">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
